import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronLeft, ChevronRight, History, Minus, Pause, Play, Plus, Timer, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ExtensionPanelProps } from '../registry';
import { useFocusStore } from './focusStore';
import { ClockDial } from './ClockDial';
import { FOCUS_ACCENT, useClick, useFocusSettings, useNow } from './hooks';
import { RollingClock } from './RollingClock';
import { primeAudio, requestNotificationPermission } from './chime';
import {
  DAY,
  HOUR,
  MINUTE,
  addDays,
  focusMsBetween,
  formatClock,
  formatDuration,
  formatHours,
  formatTimeOfDay,
  groupByDay,
  elapsedMs,
  recentIntents,
  remainingMs,
  startOfDay,
  startOfWeek,
  streakDays,
  timeInputToNextTs,
  timelineBlock,
  toTimeInputValue,
  type FocusKind,
  type FocusSession,
} from './time';

type Tab = 'timer' | 'history' | 'calendar';

const TABS: Array<{ id: Tab; label: string; Icon: React.ElementType }> = [
  { id: 'timer', label: 'Timer', Icon: Timer },
  { id: 'history', label: 'History', Icon: History },
  { id: 'calendar', label: 'Calendar', Icon: CalendarDays },
];

/** Launcher panel: set up and run a timer, review history, see the week. */
export function FocusPanel({ open, onOpenChange }: ExtensionPanelProps) {
  const [tab, setTab] = useState<Tab>('timer');
  const sync = useFocusStore((s) => s.sync);
  const setFloaterHidden = useFocusStore((s) => s.setFloaterHidden);

  useEffect(() => {
    if (!open) return;
    void sync();
    // Opening the panel is how a hidden floating timer comes back.
    setFloaterHidden(false);
  }, [open, sync, setFloaterHidden]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl focus:outline-none"
        // Let the intent field take focus instead of the first tab.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Focus timer</DialogTitle>
        <div className="flex items-center justify-center pt-4 pb-1">
          <div className="inline-flex rounded-full bg-muted/70 p-0.5" role="tablist">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  tab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="min-h-[500px]">
          {tab === 'timer' && <TimerTab onClose={() => onOpenChange(false)} />}
          {tab === 'history' && <HistoryTab />}
          {tab === 'calendar' && <CalendarTab />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Timer
// ---------------------------------------------------------------------------

function TimerTab({ onClose }: { onClose: () => void }) {
  const active = useFocusStore((s) => s.active);
  return active ? <RunningTimer /> : <TimerSetup onStarted={onClose} />;
}

function stepFor(minutes: number, dir: 1 | -1): number {
  if (dir === -1) return minutes <= 5 ? 1 : 5;
  return minutes < 5 ? 1 : 5;
}

function TimerSetup({ onStarted }: { onStarted: () => void }) {
  const settings = useFocusSettings();
  const sessions = useFocusStore((s) => s.sessions);
  const start = useFocusStore((s) => s.start);
  const [kind, setKind] = useState<FocusKind>('focus');
  const [minutes, setMinutes] = useState(settings.focusMinutes);
  const [intent, setIntent] = useState('');
  // Each second: the clock's second hand ticks
  const now = useNow(true);
  const click = useClick();
  const recents = useMemo(() => recentIntents(sessions), [sessions]);

  const switchKind = (k: FocusKind) => {
    setKind(k);
    setMinutes(k === 'focus' ? settings.focusMinutes : settings.breakMinutes);
  };

  const clamp = (m: number) => Math.min(240, Math.max(1, Math.round(m)));
  const endsAt = now + minutes * MINUTE;

  const begin = () => {
    primeAudio();
    click();
    requestNotificationPermission();
    start({ intent: kind === 'break' ? intent || 'Break' : intent, minutes, kind });
    onStarted();
  };

  return (
    <form
      className="flex flex-col items-center px-6 pb-6 pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        begin();
      }}
    >
      {/* The plan on the clock: from the minute hand round to when it would end. Drag round to set it. */}
      <ClockDial
        size={252}
        now={now}
        aheadMs={minutes * MINUTE}
        muted={kind === 'break'}
        onSetMinutes={(m) => {
          // A detent per minute, a firmer one every five
          if (m !== minutes) click(m % 5 === 0 ? 'major' : 'minor');
          setMinutes(m);
        }}
      />
      <RollingClock ms={minutes * MINUTE} className="mt-3 text-[40px] font-extralight leading-none tracking-tight" />
      <EndTimeInput
        value={endsAt}
        onChange={(ts) => setMinutes(clamp((ts - Date.now()) / MINUTE))}
        className="mt-2"
      />

      <div className="mt-6 flex items-center gap-3">
        <RoundButton label="Less time" onClick={() => setMinutes((m) => clamp(m - stepFor(m, -1)))}>
          <Minus className="h-4 w-4" />
        </RoundButton>
        <div className="flex gap-1">
          {(kind === 'focus' ? [15, 25, 50, 90] : [5, 10, 15]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                click();
                setMinutes(m);
              }}
              className={cn(
                'h-7 min-w-[36px] rounded-full px-2 text-xs tabular-nums transition-colors',
                minutes === m ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {m}
            </button>
          ))}
        </div>
        <RoundButton label="More time" onClick={() => setMinutes((m) => clamp(m + stepFor(m, 1)))}>
          <Plus className="h-4 w-4" />
        </RoundButton>
      </div>

      <div className="mt-6 w-full">
        <input
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder={kind === 'focus' ? 'What are you working on?' : 'Break'}
          maxLength={120}
          autoFocus
          className="w-full border-b border-border/70 bg-transparent pb-2 text-center text-sm outline-none placeholder:text-muted-foreground/70 focus:border-foreground/40"
        />
        {kind === 'focus' && recents.length > 0 && (
          <div className="mt-2 flex flex-wrap justify-center gap-1">
            {recents.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setIntent(r)}
                className="max-w-[160px] truncate rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex w-full items-center gap-2">
        <div className="inline-flex rounded-full bg-muted/70 p-0.5">
          {(['focus', 'break'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => switchKind(k)}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs capitalize transition-colors',
                kind === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
              )}
            >
              {k}
            </button>
          ))}
        </div>
        <button
          type="submit"
          className="ml-auto flex h-9 items-center gap-1.5 rounded-full px-5 text-sm font-medium transition-opacity hover:opacity-90"
          style={{ backgroundColor: kind === 'focus' ? FOCUS_ACCENT : 'hsl(var(--foreground))', color: kind === 'focus' ? '#fff' : 'hsl(var(--background))' }}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
          Start
        </button>
      </div>
    </form>
  );
}

function RunningTimer() {
  const active = useFocusStore((s) => s.active)!;
  const { extend, setEndsAt, setIntent, pause, resume, finish, stop, start } = useFocusStore();
  const settings = useFocusSettings();
  const now = useNow(!active.pausedAt);
  // The clock keeps time while the timer is paused
  const clockNow = useNow(true);
  const click = useClick();
  const left = remainingMs(active, now);
  const ringing = !!active.rangAt;

  return (
    <div className="flex flex-col items-center px-6 pb-6 pt-3">
      {/* What you have done, in yellow up to the minute hand; what is left runs on, fainter */}
      <ClockDial
        size={252}
        now={clockNow}
        doneMs={elapsedMs(active, clockNow)}
        aheadMs={Math.max(0, left)}
        ringing={ringing}
        muted={active.kind === 'break'}
      />
      <span className="mt-3 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        {ringing ? "Time's up" : active.pausedAt ? 'Paused' : active.kind === 'break' ? 'Break' : 'Focus'}
      </span>
      <RollingClock ms={left} className="mt-1 text-[40px] font-extralight leading-none tracking-tight" />
      <div className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
        <span>{formatTimeOfDay(active.startedAt)}</span>
        <span aria-hidden>→</span>
        <EndTimeInput value={active.endsAt} onChange={setEndsAt} />
      </div>

      <input
        value={active.intent}
        onChange={(e) => setIntent(e.target.value)}
        placeholder="What are you working on?"
        maxLength={120}
        className="mt-6 w-full bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground/70"
      />

      {ringing ? (
        <div className="mt-6 flex w-full flex-col gap-2">
          <div className="flex gap-2">
            <PillButton onClick={() => extend(5 * MINUTE)}>+5 min</PillButton>
            <PillButton onClick={() => extend(10 * MINUTE)}>+10 min</PillButton>
            {active.kind === 'focus' && (
              <PillButton
                onClick={() => {
                  finish();
                  start({ intent: 'Break', minutes: settings.breakMinutes, kind: 'break' });
                }}
              >
                {settings.breakMinutes} min break
              </PillButton>
            )}
          </div>
          <PillButton primary onClick={() => finish()}>
            <Check className="h-4 w-4" /> Done
          </PillButton>
        </div>
      ) : (
        <>
          <div className="mt-6 flex items-center gap-4">
            <RoundButton label="Remove 5 minutes" onClick={() => extend(-5 * MINUTE)} size="lg">
              <span className="text-xs tabular-nums">−5</span>
            </RoundButton>
            <button
              type="button"
              aria-label={active.pausedAt ? 'Resume' : 'Pause'}
              onClick={() => {
                click();
                if (active.pausedAt) resume();
                else pause();
              }}
              className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-sm transition-transform active:scale-95"
              style={{ backgroundColor: FOCUS_ACCENT }}
            >
              {active.pausedAt ? <Play className="h-5 w-5 fill-current" /> : <Pause className="h-5 w-5 fill-current" />}
            </button>
            <RoundButton label="Add 5 minutes" onClick={() => extend(5 * MINUTE)} size="lg">
              <span className="text-xs tabular-nums">+5</span>
            </RoundButton>
          </div>
          <div className="mt-6 flex gap-5 text-xs text-muted-foreground">
            <button type="button" className="hover:text-foreground" onClick={() => stop()}>
              Stop
            </button>
            <button type="button" className="hover:text-foreground" onClick={() => finish()}>
              Finish now
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Shows a time of day; click to type a new end time. */
function EndTimeInput({ value, onChange, className }: { value: number; onChange: (ts: number) => void; className?: string }) {
  return (
    <label className={cn('relative inline-flex cursor-pointer items-center text-[11px] text-muted-foreground hover:text-foreground', className)}>
      <span className="tabular-nums underline decoration-dotted underline-offset-4">{formatTimeOfDay(value)}</span>
      <input
        type="time"
        aria-label="End time"
        value={toTimeInputValue(value)}
        onChange={(e) => {
          const ts = timeInputToNextTs(e.target.value, Date.now());
          if (ts) onChange(ts);
        }}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
    </label>
  );
}

function RoundButton({
  label,
  onClick,
  children,
  size = 'md',
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg';
}) {
  const click = useClick();
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => {
        click();
        onClick();
      }}
      className={cn(
        'flex items-center justify-center rounded-full border border-border/70 text-foreground/80 transition-colors hover:bg-muted active:scale-95',
        size === 'lg' ? 'h-11 w-11' : 'h-8 w-8',
      )}
    >
      {children}
    </button>
  );
}

function PillButton({ onClick, children, primary }: { onClick: () => void; children: React.ReactNode; primary?: boolean }) {
  const click = useClick();
  return (
    <button
      type="button"
      onClick={() => {
        click();
        onClick();
      }}
      className={cn(
        'flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full text-sm transition-opacity hover:opacity-90',
        primary ? 'text-white' : 'border border-border/70 hover:bg-muted',
      )}
      style={primary ? { backgroundColor: FOCUS_ACCENT } : undefined}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function dayLabel(day: number, now: number): string {
  const today = startOfDay(now);
  if (day === today) return 'Today';
  if (day === addDays(today, -1)) return 'Yesterday';
  return new Date(day).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

const HEAT_WEEKS = 17;

function HistoryTab() {
  const sessions = useFocusStore((s) => s.sessions);
  const now = useNow(true, 60_000);
  const groups = useMemo(() => groupByDay(sessions), [sessions]);
  const today = startOfDay(now);
  const week = startOfWeek(now);
  const best = groups.reduce((max, g) => Math.max(max, g.focusMs), 0);

  const stats = [
    { label: 'Today', value: formatHours(focusMsBetween(sessions, today, today + DAY)) },
    { label: 'This week', value: formatHours(focusMsBetween(sessions, week, addDays(week, 7))) },
    { label: 'Best day', value: formatHours(best) },
    { label: 'Streak', value: `${streakDays(sessions, now)}d` },
  ];

  return (
    <div className="flex h-[500px] flex-col">
      <div className="space-y-3 border-b border-border/60 px-4 pb-3 pt-2">
        <div className="grid grid-cols-4 gap-1.5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl bg-muted/60 px-2.5 py-2">
              <div className="text-[10px] text-muted-foreground">{s.label}</div>
              <div className="text-base font-medium tabular-nums leading-tight">{s.value}</div>
            </div>
          ))}
        </div>
        <FocusHeatmap sessions={sessions} now={now} />
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {groups.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-muted-foreground">
            Finished sessions show up here.
          </p>
        ) : (
          groups.map((g) => (
            <section key={g.day} className="mb-2">
              <div className="flex items-baseline justify-between px-3 py-1.5">
                <span className="text-xs font-medium">{dayLabel(g.day, now)}</span>
                <span className="text-[11px] tabular-nums text-muted-foreground">{formatDuration(g.focusMs)}</span>
              </div>
              {g.sessions.map((s) => (
                <SessionRow key={s.id} session={s} />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}

/** One square per day for the last few months, darker for more focus time. */
function FocusHeatmap({ sessions, now }: { sessions: FocusSession[]; now: number }) {
  const today = startOfDay(now);
  const first = addDays(startOfWeek(now), -7 * (HEAT_WEEKS - 1));
  const weeks = useMemo(
    () =>
      Array.from({ length: HEAT_WEEKS }, (_, w) =>
        Array.from({ length: 7 }, (_, d) => {
          const day = addDays(first, w * 7 + d);
          return { day, ms: day > today ? -1 : focusMsBetween(sessions, day, addDays(day, 1)) };
        }),
      ),
    [sessions, first, today],
  );
  // Four steps up to two hours; past that every day is the darkest.
  const level = (ms: number) => (ms <= 0 ? 0 : ms < 30 * MINUTE ? 1 : ms < HOUR ? 2 : ms < 2 * HOUR ? 3 : 4);
  const alpha = ['', '33', '66', 'a6', 'ff'];

  return (
    <div className="flex justify-between" role="img" aria-label="Focus time per day">
      {weeks.map((days, w) => (
        <div key={w} className="flex flex-col gap-[3px]">
          {days.map(({ day, ms }) => (
            <span
              key={day}
              title={ms >= 0 ? `${new Date(day).toLocaleDateString([], { month: 'short', day: 'numeric' })}: ${formatDuration(ms)}` : undefined}
              className={cn('h-[13px] w-[13px] rounded-[3px]', ms < 0 ? 'bg-transparent' : level(ms) === 0 && 'bg-foreground/[0.07]')}
              style={level(ms) > 0 ? { backgroundColor: `${FOCUS_ACCENT}${alpha[level(ms)]}` } : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function SessionRow({ session }: { session: FocusSession }) {
  const updateSession = useFocusStore((s) => s.updateSession);
  const deleteSession = useFocusStore((s) => s.deleteSession);
  const [draft, setDraft] = useState(session.intent);

  useEffect(() => setDraft(session.intent), [session.intent]);

  const commit = () => {
    if (draft.trim() !== session.intent) updateSession(session.id, { intent: draft.trim() });
  };

  return (
    <div className="group flex items-center gap-2.5 rounded-lg px-3 py-1.5 hover:bg-muted/50">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{
          backgroundColor: session.kind === 'break' ? 'hsl(var(--muted-foreground) / 0.4)' : FOCUS_ACCENT,
          opacity: session.completed || session.kind === 'break' ? 1 : 0.35,
        }}
        title={session.completed ? 'Completed' : 'Stopped early'}
      />
      <div className="min-w-0 flex-1">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(session.intent);
              e.currentTarget.blur();
            }
          }}
          placeholder={session.kind === 'break' ? 'Break' : 'Untitled'}
          maxLength={120}
          className="w-full truncate bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
        />
        <div className="text-[11px] tabular-nums text-muted-foreground">
          {formatTimeOfDay(session.startedAt)} → {formatTimeOfDay(session.endedAt)}
        </div>
      </div>
      <span className="text-xs tabular-nums text-muted-foreground">{formatDuration(session.focusMs)}</span>
      <button
        type="button"
        aria-label="Delete session"
        onClick={() => deleteSession(session.id)}
        className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus:opacity-100"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

const HOUR_PX = 56;
const LOG_SNAP = 5 * MINUTE;

/**
 * Time under a point on the day column, snapped to 5 minutes. Null for the
 * future, except while dragging, where it clamps to now so you can draw up to it.
 */
function timeAtY(y: number, day: number, now: number, clamp = false): number | null {
  const raw = day + (Math.max(0, y) / HOUR_PX) * HOUR;
  const snapped = day + Math.round((raw - day) / LOG_SNAP) * LOG_SNAP;
  const end = Math.min(addDays(day, 1), now);
  if (snapped > end) return clamp ? day + Math.floor((end - day) / MINUTE) * MINUTE : null;
  return snapped;
}

function CalendarTab() {
  const sessions = useFocusStore((s) => s.sessions);
  const active = useFocusStore((s) => s.active);
  const now = useNow(true, 30_000);
  const [day, setDay] = useState(() => startOfDay(Date.now()));
  const week = startOfWeek(day);
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const scrollRef = useRef<HTMLDivElement>(null);
  const logSession = useFocusStore((s) => s.logSession);
  // Drag on empty time to draw a past session, then name it
  const [draft, setDraft] = useState<{ anchor: number; from: number; to: number; naming: boolean } | null>(null);
  const [draftIntent, setDraftIntent] = useState('');

  const daySessions = useMemo(
    () => sessions.filter((s) => s.endedAt > day && s.startedAt < addDays(day, 1)),
    [sessions, day],
  );

  // Open the day scrolled to its first session, or to the current hour.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = daySessions.length ? Math.min(...daySessions.map((s) => s.startedAt)) : null;
    const anchor = first ?? (day === startOfDay(Date.now()) ? Date.now() : day + 9 * HOUR);
    el.scrollTop = Math.max(0, ((anchor - day) / HOUR) * HOUR_PX - HOUR_PX);
    // Only when the day changes, not on every new session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const isToday = day === startOfDay(now);

  return (
    <div className="flex h-[500px] flex-col">
      <div className="flex items-center justify-between px-4 pt-1">
        <button type="button" aria-label="Previous week" onClick={() => setDay(addDays(day, -7))} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setDay(startOfDay(Date.now()))}
          className="text-xs font-medium hover:text-muted-foreground"
          title="Jump to today"
        >
          {isToday ? 'Today' : new Date(day).toLocaleDateString([], { month: 'long', day: 'numeric' })}
        </button>
        <button type="button" aria-label="Next week" onClick={() => setDay(addDays(day, 7))} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 border-b border-border/60 px-3 pb-2 pt-1">
        {days.map((d) => {
          const total = focusMsBetween(sessions, d, addDays(d, 1));
          const selected = d === day;
          const today = d === startOfDay(now);
          return (
            <button
              key={d}
              type="button"
              onClick={() => setDay(d)}
              className={cn('flex flex-col items-center rounded-lg py-1.5 transition-colors', selected ? 'bg-muted' : 'hover:bg-muted/50')}
            >
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {new Date(d).toLocaleDateString([], { weekday: 'short' })}
              </span>
              <span className={cn('text-sm tabular-nums', today && 'font-semibold')} style={today ? { color: FOCUS_ACCENT } : undefined}>
                {new Date(d).getDate()}
              </span>
              <span className={cn('text-[10px] tabular-nums', total > 0 ? 'text-foreground/70' : 'text-muted-foreground/50')}>
                {formatHours(total)}
              </span>
            </button>
          );
        })}
      </div>

      <div ref={scrollRef} className="relative flex-1 overflow-y-auto">
        <div
          className="relative select-none"
          style={{ height: 24 * HOUR_PX, cursor: draft && !draft.naming ? 'row-resize' : 'crosshair' }}
          onPointerDown={(e) => {
            // Blocks and the naming form handle their own clicks
            if (e.button !== 0 || e.target !== e.currentTarget || draft?.naming) return;
            const at = timeAtY(e.clientY - e.currentTarget.getBoundingClientRect().top, day, Date.now());
            if (at === null) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            setDraft({ anchor: at, from: at, to: at, naming: false });
          }}
          onPointerMove={(e) => {
            if (!draft || draft.naming) return;
            const at = timeAtY(e.clientY - e.currentTarget.getBoundingClientRect().top, day, Date.now(), true);
            if (at === null) return;
            setDraft({ ...draft, from: Math.min(draft.anchor, at), to: Math.max(draft.anchor, at) });
          }}
          onPointerUp={() => {
            if (!draft || draft.naming) return;
            if (draft.to - draft.from < LOG_SNAP) {
              setDraft(null);
              return;
            }
            setDraftIntent('');
            setDraft({ ...draft, naming: true });
          }}
        >
          {/* Time still to come can't be logged */}
          {isToday && (
            <div
              className="pointer-events-none absolute left-14 right-0 bottom-0"
              style={{
                top: ((now - day) / HOUR) * HOUR_PX,
                backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, hsl(var(--foreground) / 0.03) 6px 7px)',
              }}
            />
          )}
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="pointer-events-none absolute left-0 right-0 flex items-start" style={{ top: h * HOUR_PX }}>
              <span className="w-14 -translate-y-1/2 pr-2 text-right text-[10px] tabular-nums text-muted-foreground">
                {h === 0 ? '' : new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: 'numeric' })}
              </span>
              <div className="flex-1 border-t border-border/40" />
            </div>
          ))}

          {daySessions.map((s) => {
            const block = timelineBlock(s.startedAt, s.endedAt, day, HOUR_PX);
            if (!block) return null;
            return (
              <CalendarBlock
                key={s.id}
                top={block.top}
                height={block.height}
                title={s.intent || (s.kind === 'break' ? 'Break' : 'Untitled')}
                meta={`${formatTimeOfDay(s.startedAt)} · ${formatDuration(s.focusMs)}`}
                variant={s.kind === 'break' ? 'break' : s.completed ? 'focus' : 'stopped'}
              />
            );
          })}

          {active &&
            (() => {
              const block = timelineBlock(active.startedAt, Math.max(active.endsAt, now), day, HOUR_PX);
              if (!block) return null;
              return (
                <CalendarBlock
                  top={block.top}
                  height={block.height}
                  title={active.intent || 'In progress'}
                  meta={`until ${formatTimeOfDay(active.endsAt)}`}
                  variant="active"
                />
              );
            })()}

          {draft &&
            (() => {
              const block = timelineBlock(draft.from, Math.max(draft.to, draft.from + LOG_SNAP), day, HOUR_PX);
              if (!block) return null;
              const save = () => {
                logSession({ intent: draftIntent, startedAt: draft.from, endedAt: draft.to });
                setDraft(null);
              };
              return (
                <div
                  className="absolute left-14 right-3 z-20 rounded-[5px] border border-dashed px-2 py-px"
                  style={{ top: block.top, height: block.height, borderColor: FOCUS_ACCENT, backgroundColor: `${FOCUS_ACCENT}1f` }}
                >
                  <div className="text-[10px] font-medium leading-[14px] tabular-nums" style={{ color: FOCUS_ACCENT }}>
                    {formatTimeOfDay(draft.from)} → {formatTimeOfDay(draft.to)} · {formatDuration(draft.to - draft.from)}
                  </div>
                  {draft.naming && (
                    <form
                      className="absolute left-0 right-0 top-full z-30 mt-1 flex items-center gap-1.5 rounded-lg border border-border/70 bg-popover p-1.5 shadow-lg"
                      onSubmit={(e) => {
                        e.preventDefault();
                        save();
                      }}
                    >
                      <input
                        autoFocus
                        value={draftIntent}
                        onChange={(e) => setDraftIntent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setDraft(null);
                          }
                        }}
                        placeholder="What were you working on?"
                        maxLength={120}
                        className="min-w-0 flex-1 bg-transparent px-1.5 text-xs outline-none placeholder:text-muted-foreground/70"
                      />
                      <button type="button" onClick={() => setDraft(null)} className="rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted">
                        Cancel
                      </button>
                      <button type="submit" className="rounded-md px-2 py-1 text-[11px] font-medium text-white" style={{ backgroundColor: FOCUS_ACCENT }}>
                        Log
                      </button>
                    </form>
                  )}
                </div>
              );
            })()}

          {isToday && (
            <div className="pointer-events-none absolute left-12 right-0 z-10 flex items-center" style={{ top: ((now - day) / HOUR) * HOUR_PX }}>
              <span className="h-2 w-2 -translate-x-1/2 rounded-full" style={{ backgroundColor: FOCUS_ACCENT }} />
              <div className="h-px flex-1" style={{ backgroundColor: FOCUS_ACCENT }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CalendarBlock({
  top,
  height,
  title,
  meta,
  variant,
}: {
  top: number;
  height: number;
  title: string;
  meta: string;
  variant: 'focus' | 'stopped' | 'break' | 'active';
}) {
  const accentBg = `${FOCUS_ACCENT}24`;
  const style: React.CSSProperties = {
    top,
    height,
    borderLeft: `2px solid ${variant === 'break' ? 'hsl(var(--muted-foreground) / 0.5)' : FOCUS_ACCENT}`,
    backgroundColor: variant === 'break' ? 'hsl(var(--muted) / 0.8)' : variant === 'stopped' ? `${FOCUS_ACCENT}12` : accentBg,
    backgroundImage:
      variant === 'active'
        ? `repeating-linear-gradient(135deg, transparent 0 6px, ${FOCUS_ACCENT}22 6px 12px)`
        : undefined,
  };
  return (
    <div className="absolute left-14 right-3 overflow-hidden rounded-[5px] px-2 py-px" style={style} title={`${title} · ${meta}`}>
      {height >= 15 && <div className="truncate text-[11px] font-medium leading-[14px]">{title}</div>}
      {height >= 32 && <div className="truncate text-[10px] leading-3 text-muted-foreground tabular-nums">{meta}</div>}
    </div>
  );
}
